import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { bookedForLine, rowStyle, titleStyle, visitPositions } from "./diaryShared";
import { AppointmentHoverCard, StatusBadge, blockLabel, useHoverCard } from "./DiaryStatus";
import { formatDate, formatTime, inZone, todayIn } from "../../services/datetime";

/**
 * The agenda: a plain chronological list, grouped by day.
 *
 * The view that survives a narrow screen and a printer, and the one to read
 * when the question is "what is coming" rather than "who is free". Days with
 * nothing on them are dropped — an agenda of empty headings tells you nothing.
 */
export default function DiaryAgendaView({ timezone, appointments, onSelect }) {
  const { t, i18n } = useTranslation();
  const today = todayIn(timezone);
  const hover = useHoverCard();

  /** Appointments grouped by branch-local date, each group in time order. */
  const groups = useMemo(() => {
    const byDate = {};

    appointments.forEach((appointment) => {
      const { date } = inZone(appointment.starts_at, timezone);
      (byDate[date] ??= []).push(appointment);
    });

    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, items]) => ({
        date,
        items: items.sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
      }));
  }, [appointments, timezone]);

  // Which rows are one customer's visit. Read off what is already loaded —
  // no extra request, and nothing here acts on a group.
  const visits = useMemo(() => visitPositions(appointments), [appointments]);

  if (groups.length === 0) {
    return (
      <div className="card p-10 text-center text-sm text-ink-2">{t("diary.agendaEmpty")}</div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.date} className="card overflow-hidden">
          <div
            className={`border-b border-line px-4 py-2 ${
              group.date === today ? "bg-accent-wash" : "bg-raised"
            }`}
          >
            <p className="text-sm font-medium text-ink">
              {formatDate(group.date, i18n.language, {
                weekday: "long",
                month: "long",
                year: undefined,
              })}
              {group.date === today && (
                <span className="ms-2 rounded-full bg-accent-wash px-2 py-1 text-caption text-accent-text">
                  {t("diary.today")}
                </span>
              )}
            </p>
          </div>

          <ul>
            {group.items.map((a) => (
              <li key={a.id} className="border-b border-line last:border-0">
                <button
                  type="button"
                  aria-label={blockLabel(t, a)}
                  {...hover.bind(a)}
                  onClick={() => onSelect?.(a)}
                  className={`flex w-full items-center gap-3 px-3 py-3 text-start hover:bg-raised sm:px-4 ${rowStyle(a.status)}`}
                >
                  <span className="w-24 flex-shrink-0 text-sm font-medium tabular-nums" dir="ltr">
                    {formatTime(a.local_starts_at)} – {formatTime(a.local_ends_at)}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className={`truncate text-sm font-medium ${titleStyle(a.status)}`}>
                        {a.client?.name ?? t("diary.noClient")}
                        {bookedForLine(t, a) && (
                          <span className="block text-caption font-normal text-ink-2">{bookedForLine(t, a)}</span>
                        )}
                      </span>
                      {visits.has(a.id) && (
                        <span className="flex-shrink-0 rounded-full bg-raised px-2 py-0.5 text-caption text-ink-2">
                          {t("diary.visitPart", visits.get(a.id))}
                        </span>
                      )}
                    </span>
                    <span className="block truncate text-xs text-ink-2">
                      {(a.services ?? []).map((s) => s.service?.name).filter(Boolean).join(", ")}
                      {a.services?.[0]?.staff && ` · ${a.services[0].staff.display_name}`}
                    </span>
                  </span>

                  <StatusBadge status={a.status} className="max-w-[40%]" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <AppointmentHoverCard card={hover.card} />
    </div>
  );
}
